import { ChangeEvent, FunctionComponent, useCallback, useState } from "react";

import { useTranslation } from "react-i18next";

import { useForm } from "react-hook-form";

import { zodResolver } from "@hookform/resolvers/zod";

import { faCoin } from "@fortawesome/pro-regular-svg-icons";

import { Icon, Modal } from "@/components";
import {
	CustomColumnTypes,
	ListSourceType,
	PriorityFilterStatus,
	UpdateType,
} from "@/enum/list.enum";
import { InputField } from "@/components/InputField/InputField";
import { useUpdateListColumnsMutation } from "@/pages/Private/redux/list/list.api";

import { ERROR_TYPE, getAllErrors, renderErrorMessages } from "@/utils";
import { useIsFeatureActive } from "@/utils/useIsFeatureActive";
import { classNames } from "@/utils/classNames";
import { FeaturesCodes } from "@/pages/Private/pages/Users/enum/codes";

import { ButtonColor } from "@/components/Button/types";

import { CustomColumn, CustomColumnSchema, List } from "../schema/list";
import {
	CustomColumnTypeItem,
	ENRICHMENT_COLUMN_TYPES,
	PLAIN_COLUMN_TYPES,
} from "./customColumnTypeCatalog";
import { AiModal } from "./CustomColumns/Modals/AIModal";
import { JobOfferModal } from "./CustomColumns/Modals/JobOfferModal";
import { LinkedinPostModal } from "./CustomColumns/Modals/LinkedinPostModal";
import { DepartmentModal } from "./CustomColumns/Modals/DepartmentModal";
import { GoogleNewsModal } from "./CustomColumns/Modals/GoogleNewsModal";
import { TechnologyModal } from "./CustomColumns/Modals/TechnologyModal";
import { LinkedinPostEngagementModal } from "./CustomColumns/Modals/LinkedinPostEngagementModal";
import { RandomValueModal } from "./CustomColumns/Modals/RandomValueModal";
import { TextValueModal } from "./CustomColumns/Modals/TextValueModal";
import { DeepResearchModal } from "./CustomColumns/Modals/DeepResearchModal";
import { CustomColumnApplyFilters } from "./CustomColumns/customColumnApplyFilters";

export interface CreateCustomColumnModalProps {
	setOpen: (value: boolean) => void;
	open: boolean;
	listId: number;
	list?: List;
	listType?: ListSourceType;
	customColumnTypes?: CustomColumnTypes[];
	filters?: CustomColumnApplyFilters;
	selectedProspectIds?: number[];
	priorityFilterStatus?: PriorityFilterStatus[];
}

export const CreateCustomColumnModal: FunctionComponent<CreateCustomColumnModalProps> = ({
	open,
	setOpen,
	listId,
	list,
	customColumnTypes,
	filters,
	selectedProspectIds,
	priorityFilterStatus,
}) => {
	const { t } = useTranslation();

	const ts = useCallback((key: string) => t(`inbox.${key}`), [t]);
	const isDeepResearchActive = useIsFeatureActive(FeaturesCodes.DEEP_RESEARCH);
	const [columnType, setColumnType] = useState<CustomColumnTypes>();
	const [activeTab, setActiveTab] = useState<"columns" | "enrichments">("columns");

	const [update, { isLoading, error }] = useUpdateListColumnsMutation();

	const [showConfirm, setShowConfirm] = useState<boolean>(false);

	const {
		handleSubmit,
		getValues,
		setValue,
		formState: { errors },
	} = useForm<CustomColumn>({
		resolver: zodResolver(CustomColumnSchema),
	});

	const onSubmit = async (values: CustomColumn) => {
		try {
			await update({
				updateType: UpdateType.CUSTOM_COLUMN,
				id: listId,
				...values,
			}).unwrap();

			setOpen(false);
		} catch (err) {
			console.error(err);
		}
	};

	const formErrors = Object.values(errors).map((error) => error?.message) as ERROR_TYPE[];

	// A type is offered when the caller's whitelist (if any) allows it, and DEEP_RESEARCH only
	// behind its feature flag.
	const isTypeAvailable = (item: CustomColumnTypeItem) =>
		(item.id !== CustomColumnTypes.DEEP_RESEARCH || isDeepResearchActive) &&
		(!customColumnTypes || customColumnTypes.includes(item.id));

	const plainTypes = PLAIN_COLUMN_TYPES.filter(isTypeAvailable);
	const enrichmentTypes = ENRICHMENT_COLUMN_TYPES.filter(isTypeAvailable);
	const showTabs = plainTypes.length > 0 && enrichmentTypes.length > 0;
	// With one group filtered away entirely (e.g. ListEditFilter whitelists only plain types),
	// fall through to the non-empty one regardless of the selected tab.
	const effectiveTab = plainTypes.length === 0 ? "enrichments" : !showTabs ? "columns" : activeTab;
	const visibleTypes = effectiveTab === "columns" ? plainTypes : enrichmentTypes;

	const handleSelectType = (id: CustomColumnTypes) => {
		setValue("customColumnType", id);
		setColumnType(id);
	};

	const handleSwitchTab = (tab: "columns" | "enrichments") => {
		setActiveTab(tab);
		// Drop a selection made on the other tab so a hidden type can't be submitted.
		setValue("customColumnType", undefined as unknown as CustomColumnTypes);
		setColumnType(undefined);
	};

	return (
		<Modal
			handleClose={() => {
				setShowConfirm(true);
			}}
			handleSave={handleSubmit(onSubmit)}
			isLoading={isLoading}
			isOpened={open}
			overflow={true}
			size="xs"
			submitButtonText={t("basics.confirm")}
			subtitle="Select the column type to proceed to the next step."
			title="Add Column"
		>
			<div className="flex flex-col space-y-4">
				<InputField
					handleChange={function (event: ChangeEvent<HTMLInputElement>): void {
						setValue("customColumnName", event.target.value, {
							shouldValidate: true,
							shouldDirty: true,
						});
					}}
					label="Column Name"
					name={"name"}
					placeholder="Enter column name"
					value={getValues("customColumnName")}
				/>

				{showTabs && (
					<div className="flex gap-6 border-b border-gray-200" data-testid="column-modal-tabs">
						<button
							className={classNames(
								"-mb-px cursor-pointer border-b-2 pb-2 text-sm font-semibold",
								effectiveTab === "columns"
									? "border-brand-light text-brand-light"
									: "border-transparent text-text-meta hover:text-text"
							)}
							type="button"
							onClick={() => handleSwitchTab("columns")}
						>
							{ts("columnsTab")}
						</button>
						<button
							className={classNames(
								"-mb-px flex cursor-pointer items-center gap-2 border-b-2 pb-2 text-sm font-semibold",
								effectiveTab === "enrichments"
									? "border-brand-light text-brand-light"
									: "border-transparent text-text-meta hover:text-text"
							)}
							type="button"
							onClick={() => handleSwitchTab("enrichments")}
						>
							{ts("enrichmentsTab")}
							<span className="inline-flex items-center rounded-md bg-status-warning-light px-1.5 py-0.5 text-[10px] font-bold text-gray-700">
								{ts("usesCredits")}
							</span>
						</button>
					</div>
				)}

				<div
					className="max-h-[260px] overflow-auto rounded-xl border border-gray-200"
					data-testid="column-modal-type-list"
				>
					{visibleTypes.map((item) => (
						<button
							className={classNames(
								"flex w-full cursor-pointer items-center gap-2.5 border-b border-gray-100 px-3.5 py-2.5 text-left text-sm last:border-b-0",
								columnType === item.id ? "bg-blue-action-200" : "hover:bg-brand-white"
							)}
							data-testid={`column-type-${item.id}`}
							key={item.id}
							type="button"
							onClick={() => handleSelectType(item.id)}
						>
							<img alt={item.id} className="h-[20px] w-[20px]" src={item.icon} />
							<span>{item.titleOverride ?? ts(item.id)}</span>
							{item.usesCredits && (
								<span className="ml-auto inline-flex items-center gap-1 rounded-md bg-status-warning-light px-1.5 py-0.5 text-[11px] font-semibold text-gray-700">
									<Icon className="h-3 w-3" icon={faCoin} />
									{ts("creditPerRow")}
								</span>
							)}
						</button>
					))}
				</div>

				{columnType === CustomColumnTypes.RANDOM_VALUE && (
					<RandomValueModal
						customColumnName={getValues("customColumnName")}
						listId={listId}
						open={true}
						setOpen={setOpen}
						setShowConfirm={setShowConfirm}
					/>
				)}

				{columnType === CustomColumnTypes.TEXT && (
					<TextValueModal
						customColumnName={getValues("customColumnName")}
						filters={filters}
						listId={listId}
						open={true}
						priorityFilterStatus={priorityFilterStatus}
						selectedProspectIds={selectedProspectIds}
						setOpen={setOpen}
						setShowConfirm={setShowConfirm}
					/>
				)}

				{columnType === CustomColumnTypes.AI_PROMPT && (
					<AiModal
						customColumnName={getValues("customColumnName")}
						filters={filters}
						list={list}
						listId={listId}
						open={true}
						priorityFilterStatus={priorityFilterStatus}
						selectedProspectIds={selectedProspectIds}
						setOpen={setOpen}
						setShowConfirm={setShowConfirm}
					/>
				)}

				{columnType === CustomColumnTypes.JOB_OFFER && (
					<JobOfferModal
						customColumnName={getValues("customColumnName")}
						filters={filters}
						list={list}
						listId={listId}
						open={true}
						priorityFilterStatus={priorityFilterStatus}
						selectedProspectIds={selectedProspectIds}
						setOpen={setOpen}
						setShowConfirm={setShowConfirm}
					/>
				)}

				{columnType === CustomColumnTypes.TECHNOLOGY && (
					<TechnologyModal
						customColumnName={getValues("customColumnName")}
						filters={filters}
						list={list}
						listId={listId}
						open={true}
						priorityFilterStatus={priorityFilterStatus}
						selectedProspectIds={selectedProspectIds}
						setOpen={setOpen}
						setShowConfirm={setShowConfirm}
					/>
				)}

				{columnType === CustomColumnTypes.LINKEDIN_POST && (
					<LinkedinPostModal
						customColumnName={getValues("customColumnName")}
						filters={filters}
						list={list}
						listId={listId}
						open={true}
						priorityFilterStatus={priorityFilterStatus}
						selectedProspectIds={selectedProspectIds}
						setOpen={setOpen}
						setShowConfirm={setShowConfirm}
					/>
				)}

				{columnType === CustomColumnTypes.DEPARTMENT_SIZE && (
					<DepartmentModal
						customColumnName={getValues("customColumnName")}
						filters={filters}
						list={list}
						listId={listId}
						open={true}
						priorityFilterStatus={priorityFilterStatus}
						selectedProspectIds={selectedProspectIds}
						setOpen={setOpen}
						setShowConfirm={setShowConfirm}
					/>
				)}

				{columnType === CustomColumnTypes.LINKEDIN_POST_ENGAGEMENT && (
					<LinkedinPostEngagementModal
						customColumnName={getValues("customColumnName")}
						filters={filters}
						list={list}
						listId={listId}
						open={true}
						priorityFilterStatus={priorityFilterStatus}
						selectedProspectIds={selectedProspectIds}
						setOpen={setOpen}
						setShowConfirm={setShowConfirm}
					/>
				)}

				{columnType === CustomColumnTypes.GOOGLE_NEWS && (
					<GoogleNewsModal
						customColumnName={getValues("customColumnName")}
						filters={filters}
						list={list}
						listId={listId}
						open={true}
						priorityFilterStatus={priorityFilterStatus}
						selectedProspectIds={selectedProspectIds}
						setOpen={setOpen}
						setShowConfirm={setShowConfirm}
					/>
				)}

				{columnType === CustomColumnTypes.DEEP_RESEARCH && (
					<DeepResearchModal
						filters={filters}
						list={list}
						listId={listId}
						open={true}
						priorityFilterStatus={priorityFilterStatus}
						selectedProspectIds={selectedProspectIds}
						setOpen={setOpen}
						setShowConfirm={setShowConfirm}
					/>
				)}

				{getAllErrors(error, formErrors).length
					? renderErrorMessages(getAllErrors(error, formErrors))
					: null}
			</div>
			{showConfirm && (
				<Modal
					handleClose={() => setShowConfirm(false)}
					handleSave={async () => {
						setShowConfirm(false);
						setOpen(false);
					}}
					isOpened={showConfirm}
					size="xs"
					submitButtonColor={ButtonColor.ACTION}
					submitButtonText={t("basics.confirm")}
					title={t("basics.confirm")}
				>
					<>{t("basics.confirmationClose")} </>
				</Modal>
			)}
		</Modal>
	);
};
